'use client'

import { useUser } from '../../hooks/useUser'
import { usePosthogIdentify } from '../../hooks/usePosthogIdentify'
import {
  AnalyticsUser,
  AnalyticsUserField,
  useAnalyticsHandler,
  useSendAnalyticsEvent,
} from '@sylveraio/react-utils'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useState, useEffect } from 'react'

if (typeof window !== 'undefined') {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG as string, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    person_profiles: 'identified_only',
  })
}

export function useAnalyticsUserDetails(): [boolean, AnalyticsUser] {
  const { data } = useUser()
  const [userDetails, setUserDetails] = useState<AnalyticsUser>({
    uid: data?.userId,
    userEmail: data?.email,
    userOrganisation: data?.organisation,
  })
  const [hasValidUserDetails, setHasValidUserDetails] = useState(false)

  // Set the user details object
  useEffect(() => {
    setUserDetails({
      uid: data?.userId,
      userEmail: data?.email,
      userOrganisation: data?.organisation,
    })
  }, [data])

  // Ensure we have a valid user details object
  useEffect(() => {
    const userDetailFields: AnalyticsUserField[] = [
      'uid',
      'userEmail',
      'userOrganisation',
    ]
    setHasValidUserDetails(
      !userDetailFields.some(
        (field) =>
          typeof userDetails[field] === 'undefined' ||
          userDetails[field] === '',
      ),
    )
  }, [userDetails])

  return [hasValidUserDetails, userDetails]
}

export const PHProvider = ({ children }: { children: React.ReactNode }) => {
  const { sendAnalyticsEvent } = useSendAnalyticsEvent(useAnalyticsUserDetails)

  useAnalyticsHandler(sendAnalyticsEvent)
  const { data } = useUser()

  usePosthogIdentify(data)

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>
}
